import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Persisted filters for the Bids ledger (CLAUDE.md §2.3, same shape as
 * `crm.leads:ui`). Explicit filters — including Owner = Me (`mine`) — survive
 * reloads; the archived toggle and ordering stay session-only so a refresh
 * returns to the plain active list.
 */
interface BidsUiState {
  showArchived: boolean;
  setShowArchived: (v: boolean) => void;
  ordering: string | undefined;
  setOrdering: (v: string | undefined) => void;
  // Persisted filters
  mine: boolean;
  setMine: (v: boolean) => void;
  channel: string;
  setChannel: (v: string) => void;
  status: string;
  setStatus: (v: string) => void;
  origin: string;
  setOrigin: (v: string) => void;
  owner: string;
  setOwner: (v: string) => void;
  vsMedian: string;
  setVsMedian: (v: string) => void;
  rateBand: string;
  setRateBand: (v: string) => void;
  boosted: string;
  setBoosted: (v: string) => void;
  clientCountry: string;
  setClientCountry: (v: string) => void;
  clientHireRateMin: string;
  setClientHireRateMin: (v: string) => void;
  submittedFrom: string;
  submittedTo: string;
  /** Days of a rolling "Last N days" window (0 = the fixed from/to apply).
   *  Stored as a count so the window re-anchors on today after a reload. */
  submittedRolling: number;
  setSubmittedWindow: (w: { from: string; to: string; rollingDays: number }) => void;
  /** 'all' | 'yes' (has a lead) | 'no'. */
  converted: string;
  setConverted: (v: string) => void;
  /** Back to the defaults — every filter, plus the session-only toggles. */
  reset: () => void;
}

export const BIDS_FILTER_DEFAULTS = {
  mine: false,
  channel: 'all',
  status: 'all',
  origin: 'all',
  owner: '',
  vsMedian: 'all',
  rateBand: 'all',
  boosted: 'all',
  clientCountry: '',
  clientHireRateMin: '',
  submittedFrom: '',
  submittedTo: '',
  submittedRolling: 0,
  converted: 'all',
} as const;

export const useBidsUi = create<BidsUiState>()(
  persist(
    (set) => ({
      showArchived: false,
      setShowArchived: (showArchived) => set({ showArchived }),
      ordering: undefined,
      setOrdering: (ordering) => set({ ordering }),
      ...BIDS_FILTER_DEFAULTS,
      setMine: (mine) => set({ mine }),
      setChannel: (channel) => set({ channel }),
      setStatus: (status) => set({ status }),
      setOrigin: (origin) => set({ origin }),
      setOwner: (owner) => set({ owner }),
      setVsMedian: (vsMedian) => set({ vsMedian }),
      setRateBand: (rateBand) => set({ rateBand }),
      setBoosted: (boosted) => set({ boosted }),
      setClientCountry: (clientCountry) => set({ clientCountry }),
      setClientHireRateMin: (clientHireRateMin) => set({ clientHireRateMin }),
      setSubmittedWindow: ({ from, to, rollingDays }) =>
        set({ submittedFrom: from, submittedTo: to, submittedRolling: rollingDays }),
      setConverted: (converted) => set({ converted }),
      reset: () => set({ ...BIDS_FILTER_DEFAULTS, showArchived: false, ordering: undefined }),
    }),
    {
      name: 'crm.bids:ui',
      partialize: (s) => ({
        mine: s.mine,
        channel: s.channel,
        status: s.status,
        origin: s.origin,
        owner: s.owner,
        vsMedian: s.vsMedian,
        rateBand: s.rateBand,
        boosted: s.boosted,
        clientCountry: s.clientCountry,
        clientHireRateMin: s.clientHireRateMin,
        submittedFrom: s.submittedFrom,
        submittedTo: s.submittedTo,
        submittedRolling: s.submittedRolling,
        converted: s.converted,
      }),
    },
  ),
);
